// The API's structured error shape (api-contract.md §0.9), as a throwable.
//
// `message` is human text the API guarantees is safe to display and PHI-free,
// so screens surface it verbatim. That is how the three broker refusals reach
// the user now that a rejected decision writes no row: `Blocked by an active
// hold`, `Out of order — record status is <status>`, `Not authorized for this
// step` arrive as 409/409/403 messages.

export interface ApiErrorBody {
  code: string
  message: string
  details?: Record<string, unknown>
  request_id?: string
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: Record<string, unknown>
  readonly requestId?: string

  constructor(status: number, body: ApiErrorBody) {
    super(body.message || body.code || `HTTP ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.code = body.code
    this.details = body.details
    this.requestId = body.request_id
  }

  /** A state conflict the user can act on (hold, out of order, version). */
  get isConflict(): boolean {
    return this.status === 409
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError
}

/** The message to show a user for any thrown value. */
export function errorMessage(e: unknown): string {
  // A `validation_failed` reply's top-level message is only "Validation
  // failed"; the reason is in `details.errors[].message`. Show that instead,
  // so "Could not save …:" is followed by what was wrong (QA wave B, B-16).
  if (isApiError(e) && e.code === 'validation_failed') {
    const errors = e.details?.['errors']
    const messages = Array.isArray(errors)
      ? errors
          .map((entry) => (entry ?? {}) as { message?: unknown })
          .filter((entry): entry is { message: string } => typeof entry.message === 'string' && entry.message !== '')
          .map((entry) => entry.message)
      : []
    if (messages.length > 0) return messages.join(' ')
  }
  if (isApiError(e)) return e.message
  if (e instanceof Error) return e.message
  return String(e)
}

/**
 * FIELD REFUSALS WRITTEN AS WHOLE SENTENCES, which a dialog shows as written.
 * The route sends each as `validation_failed` with the sentence in
 * `details.errors[].message` (the top-level message is "Validation failed"):
 * a Coding Rule over an Inactive Code (`code_inactive`, B-12), a pair in no row
 * of the chart (`does_not_compose`), and an alias another Active rule holds
 * (`alias_taken`, D-112 / D-172).
 */
const SENTENCE_REFUSAL_CODES: readonly string[] = ['code_inactive', 'does_not_compose', 'alias_taken']

/**
 * THE SERVER'S OWN SENTENCE, when it wrote one for the person — or null, and
 * the caller adds its own "Could not save …:" lead-in. `docs/decisions.md`
 * D-161: a `conflict` (and a `duplicate_key`) is shown as written, so "Could
 * not save account:" does not stack in front of a sentence that already says
 * what blocks and what to do. The second review of QA wave B (B-16) extends
 * that to the field refusals above, which the Coding Rule editor still led
 * with "Could not save rule:". One helper, so References and every Rules tab
 * answer alike.
 */
export function refusalSentence(e: unknown): string | null {
  if (!isApiError(e)) return null
  if (e.code === 'duplicate_key' || e.code === 'conflict') return e.message
  if (e.code !== 'validation_failed') return null
  const errors = e.details?.['errors']
  if (!Array.isArray(errors)) return null
  const sentences = errors
    .filter((entry): entry is { code: string; message: string } => {
      const { code, message } = (entry ?? {}) as { code?: unknown; message?: unknown }
      return typeof code === 'string' && SENTENCE_REFUSAL_CODES.includes(code) && typeof message === 'string' && message !== ''
    })
    .map((entry) => entry.message)
  return sentences.length > 0 ? sentences.join(' ') : null
}

/** What a References or Rules editor shows when a write fails: a refusal the
    server wrote as a sentence for the person, as written (`refusalSentence`,
    D-161; B-16); anything else with the context of what was being tried. One
    copy for both screens (code review G-10). */
export function writeError(e: unknown, context: string): string {
  return refusalSentence(e) ?? `${context}: ${errorMessage(e)}`
}
